// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx, PanelLifecycleContext, ParamComponent} from 'panel';
import * as React from 'react';
import {flushSync} from 'react-dom';
import ReactDOM from 'react-dom/client';
import {Attrs, Hooks, VNode} from 'snabbdom';
import type {JsxVNodeChildren, JsxVNodeProps} from 'snabbdom-jsx-lite';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SegmentFilterControllerProvider} from 'common/contexts/react/segment-filter-controller-context';
import {ReactErrorBoundary} from 'common/components/react-error-boundary';
import {ReportBookmarkManagerProvider} from 'common/report/insights/contexts/report-bookmark-manager/provider';

import {getReactComponentId, Providers} from './react';

interface Params {
  props: any;
}

interface JsxProps {
  params: Params;
}

/**
 * These are contexts that react2panel is allowed to subscribe to.
 * DO only add legacy local Panel contexts here. The goal is to not rely on this method to subscribe to contexts and use React Hooks where possible.
 * DO NOT add any "root" level Panel contexts. Those should be subscribed to via hooks in the React component.
 */
const AllowedContexts = {
  segmentFilterController: SegmentFilterControllerProvider,
  reportBookmark: ReportBookmarkManagerProvider,
  urlManager: null,
} as const satisfies Partial<Record<keyof IronContextRegistry, React.ComponentType<React.ProviderProps<any> | null>>>;

interface Options<Contexts extends Array<keyof typeof AllowedContexts>> {
  /** Contexts to subscribe to. */
  contexts?: Contexts;
  /** Optional styles to add to the wrapper "content" div */
  wrapperStyles?: Record<string, string>;
  /**
   * Use the shadow dom.
   *
   * WARNING: Do not disable this unless you have removed host styles from your component. It might affect other components!
   */
  useShadowDom?: boolean;
  /**
   * Render the React content before the next frame when the element connects, instead of in a later
   * scheduled task. The render runs in a microtask so it never lands inside a React commit.
   */
  syncMount?: boolean;
}

interface PanelProps {
  styleOverride?: string;
  /** Attributes set on the host custom element itself rather than passed to the React component. */
  hostAttrs?: Record<string, string | number | boolean | undefined>;
}

class EmptyPanelContext implements PanelLifecycleContext {
  bindToComponent(_component: any): void {}
  unbindFromComponent(_component: any): void {}
}

/**
 * Wraps a React component with a Panel component.
 */
export function react2panel<
  Component extends React.JSXElementConstructor<any>,
  Contexts extends Array<keyof typeof AllowedContexts>,
>(
  name: string,
  reactComponent: Component,
  opts?: Options<Contexts>,
): (
  props: Pick<JsxVNodeProps, `sel` | `hook` | `class`> &
    PanelProps &
    Omit<React.ComponentPropsWithRef<Component>, Contexts[number]>,
) => VNode {
  const componentId = (reactComponent as any).componentId || getReactComponentId();

  class ReactRoot extends ParamComponent<Params, unknown, IronContextRegistry> {
    private declare rootEl: HTMLElement;
    private declare reactRoot: ReactDOM.Root;

    get config() {
      return {
        params: {
          props: Object,
        },
        ...(opts?.contexts
          ? {
              contexts: opts.contexts,
              // we want to allow React components where there's an undefined Panel context up in the DOM tree
              defaultContexts: Object.fromEntries(opts.contexts.map((context) => [context, new EmptyPanelContext()])),
            }
          : {}),
        useShadowDom: opts?.useShadowDom,
        template: () => (
          <div
            style={{
              // make sure the root isn't causing any styling changes
              display: `contents`,
              ...(opts?.wrapperStyles ? opts.wrapperStyles : {}),
            }}
            hook={{
              insert: (vnode) => {
                this.rootEl = vnode.elm as HTMLElement;
              },
            }}
          />
        ),
        hooks: {
          postUpdate: () => this.render(),
        },
      };
    }

    /**
     * Renders our React component.
     */
    render() {
      if (!this.isConnected) {
        return;
      }

      const props = this.params.props;

      const contextValues = Object.fromEntries(
        // create a new Proxy on each render so that the consumed value always triggers a prop update when passed into a PureComponent
        opts?.contexts
          ?.map((context) => {
            try {
              const value = this.getContext(context);

              if (value instanceof EmptyPanelContext) {
                return null;
              }
            } catch (_error) {
              return null;
            }

            return [context, new Proxy(this.getContext(context), {})];
          })
          .filter(Boolean) || [],
      );

      // add contexts to props
      let element: React.ReactNode = React.createElement(reactComponent, {
        ...props,
        ...contextValues,
      });

      // wrap component in providers
      const providers = Object.entries(contextValues)
        .map(([context, value]) => {
          const Provider = AllowedContexts[context];
          return Provider ? React.createElement(Provider, {value}) : null;
        })
        .filter(Boolean);

      if (providers.length) {
        element = React.createElement(Providers, {providers, children: element});
      }
      // wrap with error boundary
      element = React.createElement(ReactErrorBoundary, {children: element, minimal: true});

      this.reactRoot.render(element);
    }

    connectedCallback(): void {
      super.connectedCallback();

      // set up the react root
      this.reactRoot = ReactDOM.createRoot(this.rootEl);
      if (opts?.syncMount) {
        queueMicrotask(() => flushSync(() => this.render()));
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.reactRoot.unmount();
    }
  }

  const panelTagName = `${name}-${componentId}`;
  const ReactRootEl = defineAndMakeJsxFactory<JsxProps>(panelTagName, ReactRoot);

  const Component = (props, children?: JsxVNodeChildren) => {
    const {sel, hook, hostAttrs, ...rest} = props ?? {};

    // Panel-form callers reach this shim through the panel JSX runtime (vite/plugins/panel.mts),
    // which passes JSX children BOTH positionally AND duplicated into props.children. snabbdom
    // hands those children through as vnodes: an ELEMENT vnode (truthy `sel`/`elm`) can't be
    // rendered by React (React error #31), so it must be patched onto the host custom element
    // (where a shadow-DOM <slot> projects it) and stripped from the React props. A TEXT vnode
    // (`text` set, no `sel`) is just a string label (e.g. RadioButtonEl's country name) that
    // React renders directly, so it is unwrapped to its string and left in props for the FC.
    const isElementVNode = (child: unknown): boolean =>
      typeof child === `object` &&
      child !== null &&
      Boolean((child as {sel?: unknown}).sel || (child as {elm?: unknown}).elm);
    const toReactChild = (child: unknown): unknown =>
      typeof child === `object` && child !== null && `text` in child ? (child as {text?: unknown}).text : child;
    const rawChildren = children ?? rest.children;
    const childList = Array.isArray(rawChildren) ? rawChildren : [rawChildren];
    const vnodeChildren = childList.filter(isElementVNode);
    const reactChildren = childList
      .filter((child) => child !== null && child !== undefined && !isElementVNode(child))
      .map(toReactChild);
    if (vnodeChildren.length > 0) {
      // A mixed child list keeps its React-node siblings in props (the single-child shape
      // the FC expects) while the vnodes are forwarded to the host; a pure-vnode list drops
      // props.children entirely so no raw vnode ever reaches React (React error #31).
      if (reactChildren.length > 0) {
        rest.children = reactChildren.length === 1 ? reactChildren[0] : reactChildren;
      } else {
        delete rest.children;
      }
    }
    // snabbdom sets an attr whose value changes to `undefined` to the string "undefined" rather than
    // removing it, so unset host attrs are left out and snabbdom removes them as absent keys.
    const definedHostAttrs: Attrs | undefined =
      hostAttrs &&
      Object.fromEntries(
        Object.entries(hostAttrs as PanelProps[`hostAttrs`]).filter(
          (entry): entry is [string, Attrs[string]] => entry[1] !== undefined,
        ),
      );
    const jsxProps = {sel, hook, attrs: definedHostAttrs, params: {props: rest}};
    return vnodeChildren.length > 0 ? jsx(ReactRootEl, jsxProps, vnodeChildren) : jsx(ReactRootEl, jsxProps);
  };

  /** Accesses the Panel wrapper's tag name. ONLY rely on this for legacy use cases. */
  Component.panelTagName = panelTagName;

  return Component;
}

/** Normalizes the children a Panel JSX call passes a shim (one vnode, a list, or none) to a list. */
export function panelChildrenToList(children?: JsxVNodeChildren): VNode[] {
  if (children === null || children === undefined) {
    return [];
  }
  return (Array.isArray(children) ? children : [children]) as VNode[];
}

/**
 * Chains hooks that apply `getStyle()` to the react2panel host element, which is an inline custom
 * element the wrapped component's own styles can't reach. Reapplied on every patch so styles that
 * depend on props stay current.
 */
export function withHostStyle(hook: Hooks | undefined, getStyle: () => Partial<CSSStyleDeclaration>): Hooks {
  const applyStyle = (vnode: VNode) => Object.assign((vnode.elm as HTMLElement).style, getStyle());
  return {
    ...hook,
    insert: (vnode) => {
      applyStyle(vnode);
      hook?.insert?.(vnode);
    },
    postpatch: (oldVnode, vnode) => {
      applyStyle(vnode);
      hook?.postpatch?.(oldVnode, vnode);
    },
  };
}
